import type { AppConfig, Category } from './types';

const DEFAULTS: AppConfig = {
  app: {
    name: 'AutoParts Vision',
    shortName: 'Vision v2',
    tagline: 'AI Scanner',
    description: 'Identifique produtos por foto',
  },
  brand: {
    logoUrl: '/logo.png',
    faviconUrl: '/favicon-32.png',
    icon192Url: '/icon-192.png',
    icon512Url: '/icon-512.png',
    primaryColor: '#7c6ef5',
    backgroundColor: '#1a1a2e',
  },
  poweredBy: { name: 'iTalents', url: 'https://italents.com.br' },
  api: {
    baseUrl: '',
    predictEndpoint: '/predict',
    livePredictEndpoint: '/api/live-predict',
    vehiclesEndpoint: '/vehicles',
    feedbackEndpoint: '/feedback',
    imagesEndpoint: '/images',
  },
  categories: [
    { id: 'amortecedor', icon: '\u{1F527}', label: 'Amortecedor' },
    { id: 'arrefecimento', icon: '\u{1F321}\u{FE0F}', label: 'Arrefecimento' },
    { id: 'direcao', icon: '\u{1F504}', label: 'Direcao' },
    { id: 'eletrica', icon: '\u{26A1}', label: 'Eletrica e Ignicao' },
    { id: 'embreagem', icon: '\u{2699}\u{FE0F}', label: 'Embreagem' },
    { id: 'filtro', icon: '\u{1FAE7}', label: 'Filtro' },
    { id: 'freio', icon: '\u{1F6D1}', label: 'Freio' },
    { id: 'iluminacao', icon: '\u{1F4A1}', label: 'Iluminacao' },
    { id: 'motor', icon: '\u{1F3CE}\u{FE0F}', label: 'Motor' },
    { id: 'suspensao', icon: '\u{1F529}', label: 'Suspensao' },
  ],
  labels: {
    vehicleScreenTitle: 'Configure seu Veiculo',
    vehicleMakeLabel: 'Marca',
    vehicleMakePlaceholder: 'Selecione a marca',
    vehicleModelLabel: 'Modelo',
    vehicleModelPlaceholder: 'Selecione o modelo',
    vehicleYearLabel: 'Ano (opcional)',
    vehicleYearPlaceholder: 'Ex: 2019',
    vehicleTextLabel: 'Ou pesquise',
    vehicleTextPlaceholder: 'Ex: Onix 2019',
    btnContinue: 'Continuar',
    btnSkip: 'Pular esta etapa',
    arModeTitle: 'Iniciar Scanner AR',
    arModeSubtitle: 'Deteccao em Tempo Real',
    dividerText: 'ou envie uma foto',
    btnCamera: 'Camera',
    btnGallery: 'Galeria',
    btnIdentify: 'Identificar Produto',
    btnCapture: 'Capturar',
    btnAnalyze: 'Analisar',
    btnStop: 'Encerrar Scanner',
    btnNewScan: 'Nova Busca',
    resultLabel: 'Produto Identificado',
    scoresTitle: 'Matriz de Confianca',
    matchesTitle: 'Produtos Encontrados',
    spinnerText: 'Analisando...',
    arGuidanceInitial: 'Aponte a camera para a peca',
    arGuidanceLoading: 'Carregando modelo de IA...',
    arGuidanceDownloading: 'Baixando modelo',
    arGuidanceInitializing: 'Inicializando...',
    arGuidanceReady: 'Modelo pronto. Aponte para a peca.',
    arGuidanceSearching: 'Buscando produto...',
    arGuidanceFound: 'Produto encontrado! Toque em Analisar.',
    arGuidanceError: 'Erro de conexao. Use Capturar.',
    arGuidanceDetected: 'Detectado! Capturando...',
    arGuidanceAlmost: 'Quase lah... mantenha firme',
    arGuidanceApproach: 'Aproxime ou reposicione...',
    arGuidanceFallback: 'Usando servidor remoto',
    arLightLabel: 'LUZ',
    arStatusLoading: 'LOADING',
    arStatusReady: 'READY',
    arStatusLocked: 'LOCKED',
    arStatusScanning: 'SCANNING',
    arStatusFallback: 'FALLBACK',
    arStatusDetecting: 'DETECTANDO...',
    arStatusDefault: '--',
    errorNoCamera: 'Nao foi possivel acessar a camera.',
    errorTimeout: 'Servidor demorou. Tente novamente.',
    errorUnexpected: 'Erro inesperado. Tente outra foto.',
    errorNoConnection: 'Sem conexao. Verifique sua internet.',
    errorGeneric: 'Erro ao processar a imagem.',
    badgeCompatible: 'Compativel',
    badgeFor: 'Para',
    badgeCheck: 'Verificar compatibilidade',
    labelLowConfidence: 'Baixa confianca',
    labelMedConfidence: 'Confianca media',
    labelHighConfidence: 'Alta confianca',
    labelIdentifying: 'Identificando...',
    labelViewDetails: 'Ver detalhes',
    labelPrice: 'a vista',
    labelInstallment: 'x R$',
    labelSourceAutoz: 'Ver na AutoZ',
    labelSourceGeneric: 'Ver produto',
    labelClearPreview: 'Remover imagem',
    ariaProcessing: 'Processando',
    arGuidanceAim: 'MIRE A PECA',
    arGuidanceStable: 'Detectado! Capture ou analise.',
    watermarkBrand: 'AutoZ',
    watermarkAi: 'Identificado por IA',
  },
  theme: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontMono: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
    buttonGradient: 'linear-gradient(135deg, #4338ca, #6366f1, #818cf8)',
    fontArTitle: "'Syne', sans-serif",
    fontArMono: "'DM Mono', monospace",
  },
  ar: {
    captureThreshold: 0.75,
    confidenceHigh: 0.6,
    confidenceMedium: 0.5,
    confidenceLow: 0.4,
    predictCooldownMs: 3000,
    consecutiveThreshold: 3,
    dominanceRatio: 1.5,
    minValidConfidence: 0.3,
    zoneConfidenceThreshold: 0.4,
    debounceFrames: 3,
    debounceConfidenceThreshold: 0.6,
    brightnessLow: 60,
    brightnessWarning: 120,
  },
  pricing: {
    pixDiscount: 0.9,
    installments: 6,
    installmentMinValue: 30,
    currency: { symbol: 'R$', locale: 'pt-BR' },
  },
  cache: { dbName: 'autoparts-cache', stores: ['products', 'meta'], ttlMs: 86400000 },
  model: { onnxUrl: '/model/yolo_cls/exported/best_browser.onnx', expectedSizeBytes: 5868114 },
};

function deepMerge<T extends Record<string, unknown>>(base: T, overrides: Partial<T>): T {
  const result: Record<string, unknown> = { ...base };
  for (const k of Object.keys(overrides) as string[]) {
    const bv = base[k];
    const ov = overrides[k];
    if (
      bv !== undefined &&
      ov !== undefined &&
      typeof bv === 'object' &&
      !Array.isArray(bv) &&
      typeof ov === 'object' &&
      !Array.isArray(ov) &&
      bv !== null &&
      ov !== null
    ) {
      result[k] = deepMerge(bv as Record<string, unknown>, ov as Record<string, unknown>);
    } else if (ov !== undefined) {
      result[k] = ov;
    }
  }
  return result as T;
}

let _config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  try {
    const res = await fetch('/config/brand.json');
    const data: Partial<AppConfig> = res.ok ? await res.json() : {};
    _config = deepMerge(DEFAULTS as unknown as Record<string, unknown>, data as Partial<Record<string, unknown>>) as unknown as AppConfig;
  } catch {
    _config = DEFAULTS;
  }
  return _config!;
}

export function getCategoryLabel(cat: string): string {
  return _config?.categories.find((c: Category) => c.id === cat)?.label ?? cat;
}

export function translateCategory(cat: string): string {
  return _config?.categories.find((c: Category) => c.id === cat)?.label ?? cat;
}

export function getApiUrl(endpoint: string): string {
  return (_config?.api?.baseUrl ?? '') + endpoint;
}
